import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Checkbox,
  message,
  Row,
  Col,
  Input,
  Tooltip,
  Form,
  AutoComplete,
} from "antd";
import { CheckOutlined, LeftOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import API_MANAGER from "../../../API";
import ThreeDSafetyLogo from "../../../Assets/3d_safety_logo.png";

const { Title } = Typography;

const ProfessionalLogo = ({ src, alt, size = 64, isSelected = false }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const getCompanyInitials = (companyName) => {
    if (!companyName) return "C";
    const words = companyName.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("");
  };

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#f8f9fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: isSelected ? "3px solid #facc15" : "2px solid #e9ecef",
        transition: "all 0.3s ease",
        boxShadow: isSelected
          ? "0 0 0 3px rgba(250, 204, 21, 0.2), 0 6px 16px rgba(0, 0, 0, 0.15)"
          : "0 3px 10px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        minWidth: size,
        minHeight: size,
      }}
    >
      {imageLoading && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
          <div style={{ width: 20, height: 20, border: "2px solid #e9ecef", borderTop: "2px solid #007bff", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        </div>
      )}
      {!imageError ? (
        <img
          src={src}
          alt={alt}
          onLoad={() => { setImageLoading(false); setImageError(false); }}
          onError={() => { setImageLoading(false); setImageError(true); }}
          style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: imageLoading ? "none" : "block", padding: "4px" }}
        />
      ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#007bff", color: "white", fontSize: size * 0.4, fontWeight: "bold", fontFamily: "Arial, sans-serif" }}>
          {getCompanyInitials(alt)}
        </div>
      )}
    </div>
  );
};

const CandidateGrid = ({ candidates, selectedKeys, onToggle, releasedIds, initMode, platformSelected = true, showWorkerBadge = false, workerReleasedIds = [] }) => (
  <div className="candidates-scroll-container" style={{ maxHeight: 360, overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
    {candidates.length === 0 ? (
      <p style={{ fontSize: 13, color: "#6b7280", fontFamily: "Arial", fontWeight: 500, marginBottom: 12 }}>
        No candidates available.
      </p>
    ) : !platformSelected && (
      <p style={{ fontSize: 13, color: "#f59e0b", fontFamily: "Arial", fontWeight: 600, marginBottom: 12 }}>
        Select a company before sharing data.
      </p>
    )}
    <Row gutter={[16, 16]}>
      {candidates.map((candidate) => {
        const candidateId = candidate?._id || candidate?.appliedJob;
        if (!candidateId) return null;
        const isReleased = releasedIds.includes(candidateId);
        const isSelected = selectedKeys.includes(candidateId);
        const hasWorkerAccount = showWorkerBadge && platformSelected && (!!candidate?.profileId?.threeDSafetyWorkerId || workerReleasedIds.includes(candidateId));
        const isDisabled = !platformSelected || (initMode !== null && initMode !== isReleased && !isSelected);
        return (
          <Col key={candidateId} span={8}>
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: isDisabled && platformSelected ? "not-allowed" : !platformSelected ? "default" : "pointer" }}
              onClick={() => !isDisabled && onToggle(candidateId)}
            >
              <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => {}}
              />
              <span style={{ fontWeight: 500, fontFamily: "Arial", fontSize: 13, color: "#000", display: "inline-flex", alignItems: "center", gap: 4 }}>
                {showWorkerBadge ? (
                  hasWorkerAccount && (
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, border: "1px solid #2DBE74", borderRadius: "50%", padding: "3px" }}>
                      <CheckOutlined style={{ color: "#008000", fontSize: 10 }} />
                    </div>
                  )
                ) : (
                  isReleased && (
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, border: "1px solid #2DBE74", borderRadius: "50%", padding: "3px" }}>
                      <CheckOutlined style={{ color: "#008000", fontSize: 10 }} />
                    </div>
                  )
                )}
                {candidate?.profileId?.name || "Unknown Candidate"}
              </span>
            </div>
          </Col>
        );
      })}
    </Row>
  </div>
);

const ReleaseToExternalClientModal = ({ visible, onClose, onNext, candidateDetails }) => {
  const [activeTab, setActiveTab] = useState("external");

  // ── External platforms state ──
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCandidateRowKeys, setSelectedCandidateRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUnreleasing, setIsUnreleasing] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [initialReleaseMode, setInitialReleaseMode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [clientSearchQuery, setClientSearchQuery] = useState("");

  // ── 3D Safety state ──
  const [tdsSelected, setTdsSelected] = useState(false);
  const [tdsSelectedKeys, setTdsSelectedKeys] = useState([]);
  const [tdsLoading, setTdsLoading] = useState(false);
  const [tdsShowConfirm, setTdsShowConfirm] = useState(false);
  const [tdsSearchQuery, setTdsSearchQuery] = useState("");
  const [tdsReleasedIds, setTdsReleasedIds] = useState([]);

  // ── 3D Safety credentials modal ──
  const [tdsHasCredentials, setTdsHasCredentials] = useState(false);
  const [tdsCredentialsModalOpen, setTdsCredentialsModalOpen] = useState(false);
  const [tdsCredsSaving, setTdsCredsSaving] = useState(false);
  const [formTdsCreds] = Form.useForm();

  const candidateArray = Array.isArray(candidateDetails) ? candidateDetails : [candidateDetails];

  const isHardReviewOrAbove = (c) => {
    const lastState = c?.progress?.[c?.progress?.length - 1]?.state;
    return c?.roleAccepted === true || (typeof lastState === "number" && lastState >= 3);
  };

  const filteredCandidates = useMemo(() =>
    candidateArray.filter((c) =>
      isHardReviewOrAbove(c) &&
      (c?.profileId?.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [candidateArray, searchQuery]
  );

  const filteredClients = useMemo(() =>
    clients.filter((c) => (c?.name || "").toLowerCase().includes(clientSearchQuery.toLowerCase())),
    [clients, clientSearchQuery]
  );

  const renderSearchOption = (logo, name, tag) => ({
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <img
          src={logo}
          alt={name}
          style={{ width: 28, height: 28, borderRadius: 6, objectFit: "contain", background: "#f8f9fa", flexShrink: 0 }}
        />
        <span style={{ fontFamily: "Arial", fontSize: 13, fontWeight: 500, color: "#1e293b" }}>{name}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#94a3b8", fontFamily: "Arial" }}>{tag}</span>
      </div>
    ),
  });

  const searchOptions = useMemo(() => {
    const query = clientSearchQuery.toLowerCase();
    const opts = filteredClients.map((c) => ({
      value: `external::${c.value}`,
      ...renderSearchOption(c.logo, c.name, "Consumers"),
    }));
    if ("3d safety".includes(query)) {
      opts.push({
        value: "3dsafety::tds",
        ...renderSearchOption(ThreeDSafetyLogo, "3D Safety", "Partners"),
      });
    }
    return opts;
  }, [filteredClients, clientSearchQuery]);

  const handleSearchSelect = (value) => {
    if (value?.startsWith("external::")) {
      const id = value.replace("external::", "");
      setActiveTab("external");
      handleClientChange(id);
      scrollToClient(id);
    } else if (value === "3dsafety::tds") {
      setActiveTab("3dsafety");
      if (tdsHasCredentials) {
        setTdsSelected(true);
      } else {
        formTdsCreds.resetFields();
        setTdsCredentialsModalOpen(true);
      }
    }
    setClientSearchQuery("");
  };

  const tdsFilteredCandidates = useMemo(() =>
    candidateArray.filter((c) =>
      isHardReviewOrAbove(c) &&
      (c?.profileId?.name || "").toLowerCase().includes(tdsSearchQuery.toLowerCase())
    ),
    [candidateArray, tdsSearchQuery]
  );

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await API_MANAGER.getAllExternalClient();
      const resp = response?.data?.data?.map((item) => ({
        label: item?.name,
        value: item?._id,
        logo: item?.logo,
        name: item?.name,
        candidatesToRelease: item?.candidatesToRelease || [],
      }));
      setClients(resp);
    } catch (error) {
      message.error("Failed to load external clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClient && selectedCandidateRowKeys.length > 0) {
      const allReleased = selectedCandidateRowKeys.every((id) => selectedClient.candidatesToRelease.includes(id));
      setIsUnreleasing(allReleased);
    } else {
      setIsUnreleasing(false);
    }
  }, [selectedCandidateRowKeys, selectedClient]);

  const prefetchTdsCredentials = async () => {
    try {
      const response = await API_MANAGER.getUserData();
      const companyData = response?.data?.data;
      setTdsHasCredentials(
        !!(companyData?.threeDSafetyClientId && companyData?.threeDSafetyClientSecret)
      );
    } catch {
      setTdsHasCredentials(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchClients();
      prefetchTdsCredentials();
      setSelectedClientId(null);
      setSelectedClient(null);
      setSelectedCandidateRowKeys([]);
      setIsUnreleasing(false);
      setSearchQuery("");
      setClientSearchQuery("");
      setActiveTab("external");
      setTdsSelected(false);
      setTdsSelectedKeys([]);
      setTdsSearchQuery("");
      setTdsReleasedIds([]);
    }
  }, [visible]);

  // ── Click on 3D Safety tile: instant, credentials already pre-fetched ──
  const handleTdsTileClick = () => {
    if (tdsSelected) {
      setTdsSelected(false);
      setTdsSelectedKeys([]);
      return;
    }
    if (tdsHasCredentials) {
      setTdsSelected(true);
    } else {
      formTdsCreds.resetFields();
      setTdsCredentialsModalOpen(true);
    }
  };

  // ── External platforms handlers ──
  const handleClientChange = (value) => {
    const client = clients.find((c) => c.value === value);
    setSelectedClientId(value);
    setSelectedClient(client);
    setSelectedCandidateRowKeys([]);
    setIsUnreleasing(false);
    setInitialReleaseMode(null);
  };

  const handleCandidateToggle = (candidateId) => {
    const isReleased = selectedClient?.candidatesToRelease.includes(candidateId);
    const isSelected = selectedCandidateRowKeys.includes(candidateId);
    if (isSelected && selectedCandidateRowKeys.length === 1) setInitialReleaseMode(null);
    if (!isSelected && selectedCandidateRowKeys.length === 0) setInitialReleaseMode(isReleased);
    if (!isSelected && initialReleaseMode !== null && initialReleaseMode !== isReleased) {
      message.warning(`You can only select ${initialReleaseMode ? "released" : "non-released"} candidates in one action.`);
      return;
    }
    setSelectedCandidateRowKeys((prev) =>
      isSelected ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const handleConfirmRelease = async () => {
    const selectedCandidates = candidateArray.filter((c) => {
      const id = c?._id || c?.appliedJob;
      return id && selectedCandidateRowKeys.includes(id);
    });
    if (selectedCandidates.length === 0) { message.error("No valid candidates selected."); return; }
    const payload = {
      clientId: selectedClient.value,
      jobIds: selectedCandidates.map((c) => c?._id || c?.appliedJob).filter(Boolean),
    };
    setLoading(true);
    try {
      await API_MANAGER.externalClientRelease(payload);
      const updatedClient = { ...selectedClient };
      if (isUnreleasing) {
        updatedClient.candidatesToRelease = updatedClient.candidatesToRelease.filter((id) => !payload.jobIds.includes(id));
      } else {
        updatedClient.candidatesToRelease = [...new Set([...updatedClient.candidatesToRelease, ...payload.jobIds])];
      }
      setSelectedClient(updatedClient);
      setClients((prev) => prev.map((c) => (c.value === updatedClient.value ? updatedClient : c)));
      message.success(isUnreleasing ? "Candidates successfully removed." : "Candidate(s) data successfully shared.");
      onNext?.({ client: updatedClient, candidates: selectedCandidates });
      setShowConfirmationModal(false);
      setSelectedCandidateRowKeys([]);
      setInitialReleaseMode(null);
    } catch (error) {
      message.error("Failed to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── 3D Safety candidate handlers ──
  const handleTdsCandidateToggle = (candidateId) => {
    const isSelected = tdsSelectedKeys.includes(candidateId);
    setTdsSelectedKeys((prev) =>
      isSelected ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const handleTdsConfirmRelease = async () => {
    const selectedCandidates = candidateArray.filter((c) => {
      const id = c?._id || c?.appliedJob;
      return id && tdsSelectedKeys.includes(id);
    });
    if (selectedCandidates.length === 0) { message.error("No valid candidates selected."); return; }
    const ids = selectedCandidates.map((c) => c?._id || c?.appliedJob).filter(Boolean);
    const payload = { ids };
    setTdsLoading(true);
    try {
      await API_MANAGER.threeDSafetyRelease(payload);
      message.success("Candidate(s) data successfully shared to 3D Safety.");
      setTdsReleasedIds((prev) => [...new Set([...prev, ...ids])]);
      setTdsShowConfirm(false);
      setTdsSelectedKeys([]);
    } catch (error) {
      message.error("Failed to process request. Please try again.");
    } finally {
      setTdsLoading(false);
    }
  };

  const handleSaveTdsCredentials = async (values) => {
    setTdsCredsSaving(true);
    try {
      await API_MANAGER.linkExternalParty(values);
      message.success("3D Safety credentials saved successfully.");
      setTdsHasCredentials(true);
      setTdsCredentialsModalOpen(false);
      formTdsCreds.resetFields();
      setTdsSelected(true);
    } catch (error) {
      message.error("Failed to save credentials. Please try again.");
    } finally {
      setTdsCredsSaving(false);
    }
  };

  // ── Scroll logic ──
  const scrollRef = useRef(null);
  const logoRefs = useRef({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollToClient = (clientId) => {
    setTimeout(() => {
      const container = scrollRef.current;
      const el = logoRefs.current[clientId];
      if (!container || !el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const delta =
        (elRect.left - containerRect.left) - (container.offsetWidth / 2 - el.offsetWidth / 2);
      container.scrollTo({ left: container.scrollLeft + delta, behavior: "smooth" });
    }, 150);
  };

  const checkScroll = () => {
    const node = scrollRef.current;
    if (!node) return;
    setCanScrollLeft(node.scrollLeft > 0);
    setCanScrollRight(node.scrollLeft + node.offsetWidth < node.scrollWidth);
  };

  useLayoutEffect(() => {
    const ref = scrollRef.current;
    if (!ref) return;
    ref.addEventListener("scroll", checkScroll);
    const t = setTimeout(checkScroll, 100);
    return () => { ref.removeEventListener("scroll", checkScroll); clearTimeout(t); };
  }, [clients, selectedClientId, clientSearchQuery]);

  return (
    <>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .candidates-scroll-container::-webkit-scrollbar { display: none; }
        .candidates-scroll-container { -ms-overflow-style: none; scrollbar-width: none; }
        .professional-logo:hover { transform: scale(1.05); }
        .logo-scroll-container::-webkit-scrollbar { display: none; }
        .logo-scroll-container { -ms-overflow-style: none; scrollbar-width: none; }
        .header-scroll-container::-webkit-scrollbar { display: none; }
        .header-scroll-container { -ms-overflow-style: none; scrollbar-width: none; }
        .rtep_tab_btn {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.55);
          font-size: 13px;
          font-weight: 600;
          font-family: Arial, sans-serif;
          padding: 10px 0;
          min-width: 180px;
          text-align: center;
          border-radius: 8px 8px 0 0;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          bottom: -1px;
        }
        .rtep_tab_btn.active { background: #fff; color: #151d39; }
        .rtep_tab_bar {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          margin-top: 14px;
          border-bottom: 1px solid rgba(255,255,255,0.15);
        }
        .tds_tile_wrap {
          cursor: pointer;
          padding: 16px 24px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 120px;
          min-height: 120px;
          flex-direction: column;
          gap: 8px;
          transition: all 0.3s ease;
          position: relative;
        }
        .tds_tile_wrap.selected {
          background: rgba(250,204,21,0.1);
          border: 2px solid rgba(250,204,21,0.3);
        }
        .tds_tile_wrap:not(.selected) {
          background: transparent;
          border: 1px solid transparent;
        }
        .tds_tile_wrap img {
          width: 72px;
          height: 72px;
          object-fit: contain;
        }
        .tds_tile_wrap:hover { transform: scale(1.05); }
        .tds_creds_modal .ant-modal-content { border-radius: 12px; overflow: hidden; }
        .tds_creds_modal .ant-modal-header { border-bottom: 1px solid #f0f0f0; padding: 16px 20px; }
        .tds_creds_modal .ant-form-item-label > label { font-family: Arial; font-size: 13px; font-weight: 600; color: #374151; }
        .tds_creds_modal .ant-input,
        .tds_creds_modal .ant-input-affix-wrapper { border-radius: 8px; height: 40px; font-family: Arial; font-size: 13px; border-color: #d1d5db; }
        .tds_creds_modal .ant-input-affix-wrapper > .ant-input,
        .tds_creds_modal .ant-input-affix-wrapper > .ant-input:focus { height: auto; border: none !important; box-shadow: none !important; }
        .tds_creds_modal .ant-input:not(.ant-input-affix-wrapper .ant-input):focus,
        .tds_creds_modal .ant-input-affix-wrapper:focus-within { border-color: #122d8e !important; box-shadow: 0 0 0 2px rgba(18,45,142,0.1) !important; }
      `}</style>

      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        centered
        getContainer={document.body}
        closable={false}
        bodyStyle={{ padding: 0, borderRadius: 12, overflow: "hidden" }}
        className="externalclientmodal"
        style={{ borderRadius: 12 }}
        wrapClassName="externalclientmodal-wrapper"
      >
        {/* ── Dark header with tabs ── */}
        <div className="header-scroll-container" style={{ backgroundColor: "#151d39", padding: "16px 24px 0" }}>
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <Title level={5} style={{ color: "#fff", marginBottom: 0, textAlign: "left" }}>
              Release to 3rd party platforms
            </Title>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 16,
              marginTop: 14,
              borderBottom: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <div className="rtep_tab_bar" style={{ marginTop: 0, borderBottom: "none" }}>
              <button
                className={`rtep_tab_btn${activeTab === "external" ? " active" : ""}`}
                onClick={() => setActiveTab("external")}
              >
                Consumers
              </button>
              <button
                className={`rtep_tab_btn${activeTab === "3dsafety" ? " active" : ""}`}
                onClick={() => setActiveTab("3dsafety")}
              >
                Partners
              </button>
            </div>
            <AutoComplete
              value={clientSearchQuery}
              options={searchOptions}
              onChange={(val) => setClientSearchQuery(val)}
              onSelect={handleSearchSelect}
              filterOption={false}
              style={{ width: 280, marginBottom: 8 }}
              popupMatchSelectWidth={320}
              notFoundContent={
                <span style={{ fontFamily: "Arial", fontSize: 13, color: "#94a3b8" }}>No platforms found.</span>
              }
            >
              <Input
                placeholder="Search Platform"
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                allowClear
                style={{ height: 36, borderRadius: 8 }}
              />
            </AutoComplete>
          </div>
        </div>

        {/* ══ Tab: External Platforms ══ */}
        {activeTab === "external" ? (
          <>
            {/* Client logo strip */}
            <div className="header-scroll-container" style={{ backgroundColor: "#151d39", padding: "16px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {canScrollLeft && (
                  <Button shape="circle" icon={<LeftOutlined />} size="small"
                    onClick={() => scrollRef.current.scrollBy({ left: -200, behavior: "smooth" })} />
                )}
                <div ref={scrollRef} className="logo-scroll-container" style={{ display: "flex", overflowX: "hidden", gap: 20, flex: 1, alignItems: "center" }}>
                  {clients.map((client) => {
                    const isSelected = selectedClientId === client.value;
                    return (
                      <Tooltip key={client.value} title={client.name} placement="bottom">
                        <div
                          ref={(el) => { logoRefs.current[client.value] = el; }}
                          onClick={() => handleClientChange(client.value)}
                          className="professional-logo"
                          style={{ cursor: "pointer", padding: "16px 24px", borderRadius: 12, backgroundColor: isSelected ? "rgba(250,204,21,0.1)" : "transparent", border: isSelected ? "2px solid rgba(250,204,21,0.3)" : "1px solid transparent", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 120, minHeight: 120, flexDirection: "column", gap: 8, transition: "all 0.3s ease", position: "relative" }}
                        >
                          <ProfessionalLogo src={client.logo} alt={client.name} size={72} isSelected={isSelected} />
                          {isSelected && (
                            <div style={{ position: "absolute", bottom: -2, left: "50%", transform: "translateX(-50%)", width: 32, height: 4, backgroundColor: "#facc15", borderRadius: 2 }} />
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
                {canScrollRight && (
                  <Button shape="circle" icon={<RightOutlined />} size="small"
                    onClick={() => scrollRef.current.scrollBy({ left: 200, behavior: "smooth" })} />
                )}
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography.Text style={{ fontSize: 14, fontWeight: "bold", marginTop: 6, whiteSpace: "nowrap" }}>
                  Choose candidate(s) data, to share.
                </Typography.Text>
                <Input
                  placeholder="Search Candidate Name"
                  prefix={<SearchOutlined />}
                  style={{ width: 300, height: 40, borderRadius: 8, padding: "4px 12px", marginBottom: 24 }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear={true}
                />
              </div>
              <CandidateGrid
                candidates={filteredCandidates}
                selectedKeys={selectedCandidateRowKeys}
                onToggle={handleCandidateToggle}
                releasedIds={selectedClient?.candidatesToRelease || []}
                initMode={initialReleaseMode}
                platformSelected={!!selectedClientId}
              />
            </div>

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid #f0f0f0" }}>
              <Button onClick={onClose} style={{ marginRight: 12, borderRadius: 8, fontWeight: 600, border: "1px solid #151d39", color: "#151d39", background: "#fff" }}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  if (!selectedClient) { message.warning("Please select a client."); return; }
                  if (selectedCandidateRowKeys.length === 0) { message.warning("Please select at least one candidate."); return; }
                  setShowConfirmationModal(true);
                }}
                disabled={selectedCandidateRowKeys.length === 0}
                style={{ backgroundColor: "#facc15", color: "#000", fontWeight: 600, borderRadius: 8 }}
              >
                {isUnreleasing ? "Remove" : "Share"}
              </Button>
            </div>
          </>
        ) : (
          /* ══ Tab: Partners (3D Safety) ══ */
          <>
            <div className="header-scroll-container" style={{ backgroundColor: "#151d39", padding: "16px 24px" }}>
              <Tooltip title="3D Safety" placement="bottom">
                <div
                  className={`tds_tile_wrap${tdsSelected ? " selected" : ""}`}
                  onClick={handleTdsTileClick}
                >
                  <img src={ThreeDSafetyLogo} alt="3D Safety" />
                  {tdsSelected && <div style={{ position: "absolute", bottom: -2, left: "50%", transform: "translateX(-50%)", width: 32, height: 4, backgroundColor: "#facc15", borderRadius: 2 }} />}
                </div>
              </Tooltip>
            </div>
            <div style={{ padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography.Text style={{ fontSize: 14, fontWeight: "bold", marginTop: 6, whiteSpace: "nowrap" }}>
                  Choose candidate(s) data, to share.
                </Typography.Text>
                <Input
                  placeholder="Search Candidate Name"
                  prefix={<SearchOutlined />}
                  style={{ width: 300, height: 40, borderRadius: 8, padding: "4px 12px", marginBottom: 24 }}
                  value={tdsSearchQuery}
                  onChange={(e) => setTdsSearchQuery(e.target.value)}
                  allowClear={true}
                />
              </div>
              <CandidateGrid candidates={tdsFilteredCandidates} selectedKeys={tdsSelectedKeys} onToggle={handleTdsCandidateToggle} releasedIds={[]} initMode={null} platformSelected={tdsSelected} showWorkerBadge={true} workerReleasedIds={tdsReleasedIds} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid #f0f0f0" }}>
              <Button onClick={onClose} style={{ marginRight: 12, borderRadius: 8, fontWeight: 600, border: "1px solid #151d39", color: "#151d39", background: "#fff" }}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => { if (!tdsSelected) { message.warning("Please select 3D Safety platform first."); return; } if (tdsSelectedKeys.length === 0) { message.warning("Please select at least one candidate."); return; } setTdsShowConfirm(true); }}
                disabled={tdsSelectedKeys.length === 0}
                style={{ backgroundColor: "#facc15", color: "#000", fontWeight: 600, borderRadius: 8 }}
              >
                Share
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── External platforms confirmation modal ── */}
      <Modal
        className="candidateDeleteModal"
        centered
        getContainer={document.body}
        open={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        footer={null}
        title={isUnreleasing ? "Remove Candidate(s)?" : "Share candidate(s) data?"}
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Are you sure you want to {isUnreleasing ? "remove" : "share"} candidate(s) data{" "}
              {isUnreleasing ? "from" : "to"} your partner platform.
            </p>
          </Col>
        </Row>
        <Row align="middle" gutter={16} justify="end">
          <Col>
            <Button className="confirmBtn" onClick={handleConfirmRelease} loading={loading}>
              {isUnreleasing ? "Remove" : "Share"}
            </Button>
          </Col>
          <Col>
            <Button className="cancelBtn" onClick={() => setShowConfirmationModal(false)}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>

      {/* ── 3D Safety confirmation modal ── */}
      <Modal
        className="candidateDeleteModal"
        centered
        getContainer={document.body}
        open={tdsShowConfirm}
        onCancel={() => setTdsShowConfirm(false)}
        footer={null}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={ThreeDSafetyLogo} alt="3D Safety" style={{ width: 22, height: 22, borderRadius: 4, objectFit: "contain" }} />
            Share candidate(s) data?
          </div>
        }
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Are you sure you want to share candidate(s) data to 3D Safety.
            </p>
          </Col>
        </Row>
        <Row align="middle" gutter={16} justify="end">
          <Col>
            <Button className="confirmBtn" onClick={handleTdsConfirmRelease} loading={tdsLoading}>
              Share
            </Button>
          </Col>
          <Col>
            <Button className="cancelBtn" onClick={() => setTdsShowConfirm(false)}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>

      {/* ── 3D Safety credentials modal ── */}
      <Modal
        className="tds_creds_modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={ThreeDSafetyLogo} alt="3D Safety" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "contain" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#122d8e", fontFamily: "Arial" }}>3D Safety Integration</span>
          </div>
        }
        centered
        getContainer={document.body}
        footer={null}
        open={tdsCredentialsModalOpen}
        onCancel={() => {
          setTdsCredentialsModalOpen(false);
          formTdsCreds.resetFields();
        }}
        destroyOnClose
      >
        <Form form={formTdsCreds} layout="vertical" autoComplete="off" onFinish={handleSaveTdsCredentials}>
          <input type="text" name="username" autoComplete="username" style={{ display: "none" }} />
          <input type="password" name="password" autoComplete="new-password" style={{ display: "none" }} />
          <Form.Item
            label="Client ID"
            name="threeDSafetyClientId"
            rules={[{ required: true, message: "Please enter the Client ID" }]}
          >
            <Input placeholder="Enter your 3D Safety Client ID" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Client Secret"
            name="threeDSafetyClientSecret"
            rules={[{ required: true, message: "Please enter the Client Secret" }]}
          >
            <Input.Password placeholder="Enter your 3D Safety Client Secret" autoComplete="new-password" />
          </Form.Item>
          <Row justify="end" gutter={12} style={{ marginTop: 8 }}>
            <Col>
              <Button
                style={{ height: 38, padding: "0 18px", borderRadius: 8, border: "1px solid #d1d5db", fontFamily: "Arial", fontWeight: 600 }}
                onClick={() => { setTdsCredentialsModalOpen(false); formTdsCreds.resetFields(); }}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                htmlType="submit"
                loading={tdsCredsSaving}
                style={{ height: 38, padding: "0 24px", borderRadius: 8, background: "#122d8e", color: "#fff", fontFamily: "Arial", fontWeight: 600, border: "none" }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ReleaseToExternalClientModal;
